/**
 * Flagstaff brand style configuration extracted from Flagstaff.pptx OOXML.
 *
 * The deck uses the "Flagstaff 2023" color scheme, Calibri for major and
 * minor fonts, and an A4-landscape slide size.
 */

import type { ThemeColors } from "../../types";

export const FLAGSTAFF_COLORS = {
  black: "000000", // dk1
  navy: "324B64", // dk2
  white: "FFFFFF", // lt1
  lightGray: "E7E6E6", // lt2
  blue: "507396", // accent1
  paleBlue: "BCD2EE", // accent2
  green: "92D050", // accent3
  gray: "95959D", // accent4
  orange: "F49D56", // accent5
  purple: "A37BB5", // accent6
  hyperlink: "0563C1",
  followedHyperlink: "954F72",
} as const;

export const FLAGSTAFF_THEME_COLORS: ThemeColors = {
  dark1: FLAGSTAFF_COLORS.black,
  dark2: FLAGSTAFF_COLORS.navy,
  light1: FLAGSTAFF_COLORS.white,
  light2: FLAGSTAFF_COLORS.lightGray,
  accent1: FLAGSTAFF_COLORS.blue,
  accent2: FLAGSTAFF_COLORS.paleBlue,
  accent3: FLAGSTAFF_COLORS.green,
  accent4: FLAGSTAFF_COLORS.gray,
  accent5: FLAGSTAFF_COLORS.orange,
  accent6: FLAGSTAFF_COLORS.purple,
  hyperlink: FLAGSTAFF_COLORS.hyperlink,
  followedHyperlink: FLAGSTAFF_COLORS.followedHyperlink,
  majorFont: "Calibri",
  minorFont: "Calibri",
};

export const FLAGSTAFF_CHART_SERIES_PALETTE = [
  FLAGSTAFF_COLORS.blue,
  FLAGSTAFF_COLORS.orange,
  FLAGSTAFF_COLORS.green,
  FLAGSTAFF_COLORS.purple,
  FLAGSTAFF_COLORS.paleBlue,
  FLAGSTAFF_COLORS.gray,
];

export const FLAGSTAFF_SLIDE_DIMENSIONS = {
  width: 841.9,
  height: 595.2,
} as const;

export const FLAGSTAFF_POSITIONS = {
  title: { left: 58.1, top: 42.5, width: 725.8, height: 34.0 },
  contentStart: { left: 58.1, top: 147.4 },
  footnote: { left: 58.1, top: 547.1, width: 579.7, height: 28.4 },
} as const;

export const FLAGSTAFF_LAYOUT_NAMES = [
  "Title Slide",
  "3 x 1",
  "1 x 3",
  "Heading and 3-Column Content",
  "Heading and 2-Column Content with left being wider",
  "Heading and 2-Column Content with right being wider",
  "Heading and 3 x 3 Column Content",
  "Heading and 2x2 Column Content",
  "Heading and 2 x Row Content",
  "Heading and 1x2 Column Content",
  "Heading and 2x1 Column Content",
  "Title Slide - Portrait Image",
  "Heading and 1 Row x 2 Column Content",
  "Heading and 2 Column x 1 Row Content",
  "Bio",
  "Heading and Strapline Only",
  "Heading Only",
  "Blank",
  "2_SECTION",
  "1_Title Slide - Portrait Image",
  "2_Heading and 1-Column Content NEW",
  "Disclaimer Page",
  "CONTENTS",
  "CONTENTS 16 pts",
  "SECTION",
  "Appendices",
  "Heading and 1-Column Content NEW",
  "Heading and 1-Column Content no strapline",
  "Heading and 2-Column Content",
] as const;

export const FLAGSTAFF_STYLE_PROMPT = `

## FLAGSTAFF BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Flagstaff.pptx" is the authoritative Flagstaff visual standard. Match the closest relevant precedent for cover, contents, section, company profile, financial analysis, process, biography, appendix, and disclaimer pages. Prefer its established grids, title hierarchy, spacing, and footer treatment over inventing a generic layout.

CRITICAL FONT RULE: Include \`fontFamily\` on EVERY shape you create or modify. Flagstaff uses \`"Calibri"\` for titles, headings, body text, tables, and charts. If an existing shape uses another font, set it back to Calibri.

### Color palette (from the "Flagstaff 2023" master color scheme)
Use ONLY these Flagstaff colors. Do not introduce arbitrary hex values.

- Text1 / dark1: Black #${FLAGSTAFF_COLORS.black}
- Text2 / dark2: Navy #${FLAGSTAFF_COLORS.navy}
- Background1 / light1: White #${FLAGSTAFF_COLORS.white}
- Background2 / light2: Light gray #${FLAGSTAFF_COLORS.lightGray}
- Accent1: Blue #${FLAGSTAFF_COLORS.blue}
- Accent2: Pale blue #${FLAGSTAFF_COLORS.paleBlue}
- Accent3: Green #${FLAGSTAFF_COLORS.green}
- Accent4: Gray #${FLAGSTAFF_COLORS.gray}
- Accent5: Orange #${FLAGSTAFF_COLORS.orange}
- Accent6: Purple #${FLAGSTAFF_COLORS.purple}
- Chart series palette: ${FLAGSTAFF_CHART_SERIES_PALETTE.map((color) => `#${color}`).join(", ")}

Lead with blue (#${FLAGSTAFF_COLORS.blue}) for the primary series and use navy (#${FLAGSTAFF_COLORS.navy}) for strong headings and dark emphasis.

### Typography and geometry
Slide dimensions: 841.9 × 595.2 pt (A4 landscape).

- Title: left=58.1, top=42.5, width=725.8, height=34.0 — Calibri, navy or black, left-aligned
- Content area: starts at left=58.1, top=147.4
- Footer band: around top=547.1, width=579.7
- Keep body copy concise and preserve the template's generous spacing between the title, strapline, and content.

### Charts
- Use Calibri at 8pt or larger.
- Lead with blue; use the ordered chart palette for additional series.
- Prefer direct data labels and restrained gridlines.
- Avoid 3D effects, gradients, and decorative fills.

### Tables
- Use navy or blue headers with white text.
- Use white body rows with black text and optional light-gray alternate shading.
- Keep borders thin and gray; align numeric columns consistently.

### Shapes and layout
- Prefer sharp rectangular panels and thin separators.
- For ordinary body slides, use the exact layout \`"Heading and 1-Column Content NEW"\`, which carries the branded body furniture. If no specific layout is required, omit \`layoutName\` so the registry applies that layout automatically.
- Use cover-like layouts such as \`"Title Slide"\`, \`"Title Slide - Portrait Image"\`, \`"SECTION"\`, and \`"2_SECTION"\` only for their intended cover or section-divider purpose, never for body slides.
- Use another named Flagstaff column layout only when its structure is specifically required, and follow its established grid.
- Keep footers, page furniture, and logos unobstructed.

**TEXT FITTING RULE**: Keep labels and item descriptions short. Reduce copy before reducing font size, and never allow text to overflow its placeholder or panel.
`;
